import {useCallback, useContext, useState} from 'react';

import {ValidationContext} from 'src/contexts/validation-context';
import {useCommonDataHook} from 'src/hooks/use-common-data-hook';
import {getAccountIpRanges} from 'src/openapi/admin/apis/GetAccountIpRangesApi';
import {updateAccountIpRanges} from 'src/openapi/admin/apis/UpdateAccountIpRangesApi';
import {useAppDispatch} from 'src/store/hooks';

import {DEFAULT_IP_RANGE} from '../constants';

import {useValidateIPRange} from './useValidateIpRanges';


import type {ValidationContextType} from 'src/contexts/validation-context';
import type {IpRange} from 'src/openapi/admin/models/IpRange';

type Props = {
    accountId: number,
};

export const useIpRanges = ({accountId}: Props) => {
    const apiDispatch = useAppDispatch();

    const [lastEditedRange, setLastEditedRange] = useState(-1);
    const {validationErrors} = useContext<ValidationContextType>(ValidationContext);
    const {
        validateDefaultIPRange,
        validateIP,
        validateIPRange,
        validateIPRangeDuplicates,
        validateUnfilledIp,
    } = useValidateIPRange();

    const getDispatchAction = useCallback(async () => {
        const loadIpRangesResponse = await apiDispatch(getAccountIpRanges({accountId}));
        if (loadIpRangesResponse.payload.success && !loadIpRangesResponse.payload.data.length) {
            const payload: {success: true, error: false, data: IpRange[]} = {
                success: true,
                error: false,
                data: [DEFAULT_IP_RANGE],
            };

            return Promise.resolve({type: '', payload});
        } else {
            return Promise.resolve({type: '', payload: loadIpRangesResponse.payload});
        }
    }, [accountId, apiDispatch]);

    const editValidation = useCallback((ipRanges: IpRange[]) => {
        if (!validateDefaultIPRange(ipRanges)) {
            ipRanges.forEach((range, id) => {
                if (range.start && range.end) {
                    if (!validateIP(range, id)) {
                        return;
                    }

                    validateIPRange(range, id);
                }
            });

            validateIPRangeDuplicates(ipRanges, lastEditedRange);
            setLastEditedRange(-1);
        }
    }, [lastEditedRange, validateDefaultIPRange, validateIP, validateIPRange, validateIPRangeDuplicates]);

    const saveValidation = useCallback((ipRanges: IpRange[]) => {
        let valid = true;

        if (validationErrors.length) {
            valid = false;
        }

        if (
            !validateDefaultIPRange(ipRanges) &&
            ipRanges.some((range, id) => !validateUnfilledIp(range, id))
        ) {
            valid = false;
        }

        return valid;
    }, [validationErrors, validateDefaultIPRange, validateUnfilledIp]);

    const saveDispatchAction = useCallback((ipRanges: IpRange[]) => apiDispatch(
        updateAccountIpRanges({
            accountId,
            ipRange: ipRanges.filter(range => range.start && range.end),
        }),
    ), [accountId, apiDispatch]);

    return {
        ...useCommonDataHook({
            initValue: [{start: '', end: ''}],
            getDispatchAction,
            saveDispatchAction,
            editValidation,
            saveValidation,
        }),
        setLastEditedRange,
    };
};
